// UI strings for the translator's verified memory, the miss queue and "ask instead of guess"
// (Axel's choices of 2026-10-09; app/src/translator/Official.tsx, panels/NearMemory.tsx,
// panels/AskReading.tsx, panels/MissQueue.tsx). Finnish (default) and English side by side; en must
// have exactly the same shape.
import type { MissReason } from '@valyrio/engine/miss-queue';
import { defineArea } from '../area';

/** "’a’", "’a’ vai ’b’", "’a’, ’b’ vai ’c’". */
const orList = (xs: string[], or: string): string => {
  const q = xs.map((x) => `’${x}’`);
  return q.length < 2 ? (q[0] ?? '') : `${q.slice(0, -1).join(', ')} ${or} ${q.at(-1)}`;
};

/** "tarkistussessio 2026-10-12" → its date in Finnish order (12.10.2026). */
const sessionDateFi = (label: string): string => {
  const m = /(\d{4})-(\d{2})-(\d{2})/.exec(label);
  return m ? `${Number(m[3])}.${Number(m[2])}.${m[1]}` : label;
};
const sessionDateEn = (label: string): string => /(\d{4}-\d{2}-\d{2})/.exec(label)?.[1] ?? label;

export const trMemoryFi = {
  // the answer from the memory (Official.tsx)
  rulesAnyway: 'Käännä sana sanalta (epävarmempi)',
  backToMemory: 'Näytä valmis käännös',
  oursHalloween: 'Halloween-lause',
  oursSession: (label: string) => `tarkistussessio ${sessionDateFi(label)}`,
  sessionHint: 'Tarkistussessiossa tehty käännös. Se ei ole Petersonin, eikä kumpikaan teistä ole vielä hyväksynyt sitä.',
  // the closest sentence (NearMemory.tsx)
  nearTitle: 'Lähin valmis käännös',
  nearNotSame: 'Ei sama lause: korostetut sanat eroavat kirjoittamastasi.',
  // ask instead of guess (AskReading.tsx)
  askQuestion: (word: string, labels: string[]) => `«${word}»: tarkoititko ${orList(labels, 'vai')}?`,
  /** A case question names the cases plainly: «dōros»: nominatiivi vai akkusatiivi? */
  askCase: (word: string, cases: string[]) => `«${word}»: ${cases.slice(0, -1).join(', ')} vai ${cases.at(-1) ?? ''}?`,
  askRemembered: 'Käytössä on aiempi valintasi tälle sanalle.',
  askForget: 'Unohda valinta',
  go: { before: 'ennen', under: 'alla' } as Record<string, string>,
  // the miss queue (MissQueue.tsx)
  queueTitle: 'Odottaa tarkistussessiota',
  queueCount: (n: number) => (n === 1 ? '1 lause' : `${n} lausetta`),
  reasons: {
    'low-confidence': 'epävarma',
    gap: 'sana puuttuu',
    'no-finite-verb': 'verbi puuttuu',
    'self-check': 'muoto ei täsmää',
    'marked-wrong': 'merkitty vääräksi',
  } as Record<MissReason, string>,
  remove: 'Poista',
  removeAria: (text: string) => `Poista listalta: ${text}`,
  markWrong: 'Merkitse vääräksi',
  markedWrong: 'Odottaa tarkistussessiota',
  queued: 'Lause odottaa tarkistussessiota.',
};

const trMemoryEn: typeof trMemoryFi = {
  rulesAnyway: 'Translate word by word (less sure)',
  backToMemory: 'Show the ready translation',
  oursHalloween: 'Halloween line',
  oursSession: (label) => `review session ${sessionDateEn(label)}`,
  sessionHint: 'Translated in a review session. It is not Peterson’s, and neither of you has approved it yet.',
  nearTitle: 'Closest ready translation',
  nearNotSame: 'Not the same sentence: the marked words differ from yours.',
  askQuestion: (word, labels) => `“${word}”: did you mean ${orList(labels, 'or')}?`,
  askCase: (word, cases) => `“${word}”: ${cases.slice(0, -1).join(', ')} or ${cases.at(-1) ?? ''}?`,
  askRemembered: 'Your earlier choice for this word is in use.',
  askForget: 'Forget the choice',
  go: { before: 'before', under: 'under' },
  queueTitle: 'Waiting for a review session',
  queueCount: (n) => (n === 1 ? '1 sentence' : `${n} sentences`),
  reasons: {
    'low-confidence': 'unsure',
    gap: 'a word is missing',
    'no-finite-verb': 'no verb',
    'self-check': 'a form does not fit',
    'marked-wrong': 'marked wrong',
  },
  remove: 'Remove',
  removeAria: (text) => `Remove from the list: ${text}`,
  markWrong: 'Mark as wrong',
  markedWrong: 'Waiting for a review session',
  queued: 'The sentence is waiting for a review session.',
};

// Messages.trMemory (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('trMemory', trMemoryFi, trMemoryEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof trMemoryFi> {}
}
